import React from 'react';

/**
 * Lesson-player (v4) primitives — the child-facing smartboard + pupil kit.
 *
 * These are ported pixel-faithfully from the design-system handoff and use
 * inline styles + the `--c-*` / `--lp-*` CSS custom properties (see globals.css
 * and app/layout.tsx) rather than Tailwind, so the full-bleed colour language,
 * chunky pressable buttons and spring motion match the source exactly.
 */

export type Stage = 'action' | 'select' | 'practice' | 'progress' | 'write';

export const stageColorVar = (stage: Stage): string => `var(--c-${stage})`;

const DISPLAY = 'var(--lp-display), Impact, sans-serif';
const BODY = 'var(--lp-body), system-ui, sans-serif';

/* -------------------------------------------------------------------------- */
/* StageView — full-bleed coloured background with faint white spotlights      */
/* -------------------------------------------------------------------------- */

interface StageViewProps {
  stage?: Stage;
  center?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const StageView = ({
  stage = 'action',
  center = true,
  className,
  style,
  children,
}: React.PropsWithChildren<StageViewProps>) => (
  <div
    className={className}
    style={{
      position: 'relative',
      background: stageColorVar(stage),
      transition: 'background var(--dur-med) var(--ease)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: center ? 'center' : 'stretch',
      justifyContent: center ? 'center' : 'flex-start',
      minHeight: '100%',
      ...style,
    }}
  >
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundImage:
          'radial-gradient(circle at 12% 18%, rgba(255,255,255,0.07) 0, transparent 200px),' +
          'radial-gradient(circle at 88% 78%, rgba(255,255,255,0.07) 0, transparent 200px),' +
          'radial-gradient(circle at 60% 10%, rgba(255,255,255,0.05) 0, transparent 250px)',
      }}
    />
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: center ? 'center' : 'stretch',
        justifyContent: center ? 'center' : 'flex-start',
        flex: 1,
      }}
    >
      {children}
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* ProgressDots                                                                */
/* -------------------------------------------------------------------------- */

interface ProgressDotsProps {
  total?: number;
  current?: number;
  tone?: 'light' | 'dark';
}

export const ProgressDots = ({
  total = 6,
  current = 0,
  tone = 'light',
}: ProgressDotsProps) => {
  const onColor =
    tone === 'dark' ? 'var(--clay-green, #2A8049)' : 'var(--c-on-color)';
  const offColor =
    tone === 'dark' ? 'rgba(14,90,42,0.22)' : 'rgba(255,255,255,0.3)';
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      {Array.from({ length: total }).map((_, i) => {
        const complete = i < current;
        const isCurrent = i === current;
        return (
          <div
            key={i}
            style={{
              height: 14,
              width: isCurrent ? 48 : 14,
              borderRadius: isCurrent ? 8 : '50%',
              background: complete || isCurrent ? onColor : offColor,
              transition: 'all var(--dur-fast) var(--ease)',
            }}
          />
        );
      })}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* SmartboardChrome — fixed top status bar + bottom control bar                */
/* -------------------------------------------------------------------------- */

interface SmartboardChromeProps {
  brand?: string;
  lessonLabel?: string;
  total?: number;
  current?: number;
  hint?: React.ReactNode;
  left?: React.ReactNode;
  right?: React.ReactNode;
}

export const SmartboardChrome = ({
  brand = 'PICKATALE PHONICS',
  lessonLabel = 'LESSON 1 · /s/',
  total = 6,
  current = 0,
  hint,
  left,
  right,
  children,
}: React.PropsWithChildren<SmartboardChromeProps>) => (
  <div
    style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '32px 60px',
        color: 'var(--c-on-color)',
        fontFamily: DISPLAY,
        letterSpacing: '3px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          fontSize: 'clamp(14px,1.4vw,22px)',
          opacity: 0.85,
        }}
      >
        <span>{brand}</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>{lessonLabel}</span>
      </div>
      <ProgressDots total={total} current={current} />
    </div>

    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: '0 60px',
      }}
    >
      {children}
    </div>

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '0 60px 32px',
      }}
    >
      <div>{left}</div>
      {hint && (
        <div
          style={{
            fontFamily: DISPLAY,
            fontSize: 'clamp(14px,1.2vw,18px)',
            letterSpacing: '2px',
            color: 'rgba(255,255,255,0.7)',
            textAlign: 'center',
          }}
        >
          {hint}
        </div>
      )}
      <div>{right}</div>
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* PillButton — chunky pressable pill                                          */
/* -------------------------------------------------------------------------- */

type PillVariant = 'primary' | 'ghost';
type PillSize = 'sm' | 'md' | 'lg';

interface PillButtonProps {
  variant?: PillVariant;
  size?: PillSize;
  glyph?: string;
  arrow?: 'left' | 'right';
  color?: string;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const PILL_PADS: Record<PillSize, string> = {
  sm: '12px 24px',
  md: '18px 36px',
  lg: '22px 56px',
};

const PILL_FONTS: Record<PillSize, string> = {
  sm: 'clamp(14px, 1.3vw, 18px)',
  md: 'clamp(16px, 1.6vw, 26px)',
  lg: 'clamp(20px, 1.8vw, 30px)',
};

export const PillButton = ({
  variant = 'primary',
  size = 'md',
  glyph,
  arrow,
  color = 'var(--c-action)',
  disabled = false,
  onClick,
  className,
  style,
  children,
}: React.PropsWithChildren<PillButtonProps>) => {
  const isGhost = variant === 'ghost';

  const skin: React.CSSProperties = isGhost
    ? {
        background: 'rgba(255,255,255,0.18)',
        color: 'var(--c-on-color)',
        boxShadow: 'none',
        border: '2px solid rgba(255,255,255,0.3)',
      }
    : { background: '#fff', color, boxShadow: 'var(--shadow-button)' };

  const press =
    (translate: string, pressed: boolean) =>
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      e.currentTarget.style.transform = translate;
      if (!isGhost) {
        e.currentTarget.style.boxShadow = pressed
          ? 'var(--shadow-button-pressed)'
          : 'var(--shadow-button)';
      }
    };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={press('translateY(-2px)', false)}
      onMouseLeave={press('', false)}
      onMouseDown={press('translateY(4px)', true)}
      onMouseUp={press('translateY(-2px)', false)}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        border: 'none',
        fontFamily: DISPLAY,
        fontSize: PILL_FONTS[size],
        letterSpacing: '3px',
        padding: PILL_PADS[size],
        borderRadius: 'var(--radius-button)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'transform 100ms var(--ease), box-shadow 100ms var(--ease)',
        opacity: disabled ? 0.3 : 1,
        lineHeight: 1,
        ...skin,
        ...style,
      }}
    >
      {glyph && (
        <span style={{ fontSize: '1.1em', lineHeight: 0.7 }}>{glyph}</span>
      )}
      {arrow === 'left' && (
        <span style={{ fontSize: '1.4em', lineHeight: 0.7 }}>←</span>
      )}
      {children}
      {arrow === 'right' && (
        <span style={{ fontSize: '1.4em', lineHeight: 0.7 }}>→</span>
      )}
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/* LetterCard — the giant focal grapheme                                       */
/* -------------------------------------------------------------------------- */

type LetterSize = 'huge' | 'massive' | 'colossal';

const LETTER_SIZES: Record<LetterSize, string> = {
  huge: 'var(--fs-letter-huge, clamp(80px,10vw,180px))',
  massive: 'var(--fs-letter-massive, clamp(120px,16vw,280px))',
  colossal: 'var(--fs-letter-colossal, clamp(200px,26vw,480px))',
};

interface LetterCardProps {
  size?: LetterSize;
  stage?: Stage;
  style?: React.CSSProperties;
}

export const LetterCard = ({
  size = 'huge',
  stage = 'practice',
  style,
  children,
}: React.PropsWithChildren<LetterCardProps>) => (
  <div
    style={{
      background: '#fff',
      borderRadius: 'var(--radius-card)',
      fontFamily: DISPLAY,
      color: stageColorVar(stage),
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: 'var(--shadow-card)',
      lineHeight: 1,
      fontSize: LETTER_SIZES[size],
      padding: '0.05em 0.3em',
      minWidth: '1.5em',
      ...style,
    }}
  >
    {children}
  </div>
);

/* -------------------------------------------------------------------------- */
/* PictureCard — decodable-word tile with highlighted target grapheme          */
/* -------------------------------------------------------------------------- */

type PictureState = 'default' | 'yes' | 'no';

interface PictureCardProps {
  emoji: string;
  word: string;
  target?: string;
  state?: PictureState;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export const PictureCard = ({
  emoji,
  word,
  target,
  state = 'default',
  onClick,
  style,
}: PictureCardProps) => {
  const isYes = state === 'yes';
  const isNo = state === 'no';

  let before = word;
  let hit = '';
  let after = '';
  if (target && word) {
    const i = word.toLowerCase().indexOf(target.toLowerCase());
    if (i >= 0) {
      before = word.slice(0, i);
      hit = word.slice(i, i + target.length);
      after = word.slice(i + target.length);
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: isYes
          ? 'linear-gradient(135deg, var(--c-success), var(--c-success-2))'
          : '#fff',
        border: 'none',
        borderRadius: 'var(--radius-card)',
        padding: '32px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'var(--shadow-card)',
        aspectRatio: '1',
        cursor: onClick ? 'pointer' : 'default',
        opacity: isNo ? 0.35 : 1,
        transition: 'transform 150ms var(--ease)',
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 'clamp(64px, 9vw, 140px)',
          marginBottom: 12,
          lineHeight: 1,
        }}
      >
        {emoji}
      </div>
      <div
        style={{
          fontFamily: DISPLAY,
          fontSize: 'clamp(24px, 3vw, 44px)',
          letterSpacing: '4px',
          color: isYes ? '#fff' : 'var(--c-select)',
        }}
      >
        {before}
        {hit && (
          <span
            style={{
              background: 'var(--c-highlight)',
              color: '#1A1A1A',
              padding: '0 8px',
              borderRadius: 8,
            }}
          >
            {hit}
          </span>
        )}
        {after}
      </div>
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/* SoundTile — a phoneme tile for robot blending                               */
/* -------------------------------------------------------------------------- */

interface SoundTileProps {
  playing?: boolean;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
}

export const SoundTile = ({
  playing = false,
  size = 'md',
  style,
  children,
}: React.PropsWithChildren<SoundTileProps>) => {
  const dim =
    size === 'sm' ? 'clamp(90px,10vw,140px)' : 'clamp(140px,14vw,220px)';
  const font =
    size === 'sm' ? 'clamp(56px,7vw,96px)' : 'clamp(80px,10vw,140px)';
  return (
    <div
      style={{
        width: dim,
        height: dim,
        background: playing ? 'var(--c-highlight)' : '#fff',
        color: 'var(--c-progress)',
        borderRadius: 'var(--radius-card)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: DISPLAY,
        fontSize: font,
        boxShadow: playing
          ? '0 12px 40px rgba(255, 217, 61, 0.6)'
          : 'var(--shadow-card)',
        transform: playing ? 'scale(1.15)' : 'scale(1)',
        transition: 'all 200ms var(--ease)',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* SpeechBubble                                                                */
/* -------------------------------------------------------------------------- */

interface SpeechBubbleProps {
  point?: 'left' | 'right';
  color?: string;
  style?: React.CSSProperties;
}

export const SpeechBubble = ({
  point = 'left',
  color = 'var(--c-practice)',
  style,
  children,
}: React.PropsWithChildren<SpeechBubbleProps>) => {
  const tail: React.CSSProperties =
    point === 'left'
      ? {
          left: -20,
          bottom: '30%',
          borderWidth: '18px 30px 18px 0',
          borderColor: 'transparent #fff transparent transparent',
        }
      : {
          right: -20,
          bottom: '30%',
          borderWidth: '18px 0 18px 30px',
          borderColor: 'transparent transparent transparent #fff',
        };
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        background: '#fff',
        color,
        fontFamily: DISPLAY,
        padding: '24px 48px',
        borderRadius: 32,
        boxShadow: 'var(--shadow-card)',
        letterSpacing: '6px',
        fontSize: 'clamp(40px, 5vw, 96px)',
        lineHeight: 1,
        ...style,
      }}
    >
      {children}
      <span
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          borderStyle: 'solid',
          ...tail,
        }}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* RewardBadge                                                                 */
/* -------------------------------------------------------------------------- */

interface RewardBadgeProps {
  glyph?: string;
  style?: React.CSSProperties;
}

export const RewardBadge = ({
  glyph = '🎉',
  style,
  children,
}: React.PropsWithChildren<RewardBadgeProps>) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 12,
      background: 'rgba(255,255,255,0.18)',
      border: '2px solid rgba(255,255,255,0.3)',
      padding: '12px 24px',
      borderRadius: 'var(--radius-pill)',
      color: '#fff',
      fontFamily: DISPLAY,
      letterSpacing: '3px',
      fontSize: 'clamp(14px, 1.4vw, 20px)',
      ...style,
    }}
  >
    {glyph && <span style={{ fontSize: '1.4em' }}>{glyph}</span>}
    {children}
  </span>
);
